import { Check, X, User, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ActiveVerification } from "../../types";

interface PendingRequestsProps {
  requests: ActiveVerification[];
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
  cipherCode: string;
}

const PendingRequests = ({
  requests,
  onApprove,
  onDeny,
  cipherCode,
}: PendingRequestsProps) => {
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString() +
      " at " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  if (requests.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6 text-center text-gray-500">
          <p>No pending verification requests</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card key={request.id} className="border-l-4 border-l-yellow-400">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <h3 className="font-medium font-mono">🕵️ {cipherCode}</h3>
                <Badge
                  variant="outline"
                  className="bg-yellow-50 text-yellow-700 border-yellow-200"
                >
                  Pending
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">From:</span>
                  <span className="ml-2 font-mono text-xs">
                    {request.requesterId}
                  </span>
                </div>

                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">Expires:</span>
                  <span className="ml-2">{formatDate(request.expiresAt)}</span>
                </div>

                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">Request ID:</span>
                  <span className="ml-2 font-mono text-xs">{request.id}</span>
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-end space-x-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDeny(request.id)}
            >
              <X className="h-4 w-4 mr-1" />
              Deny
            </Button>

            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onApprove(request.id)}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default PendingRequests;
